import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { SPACE_RECORDS_SELECTION_TARGET } from '../../../constants'
import type { Plugin } from '../plugin'

/**
 * Initializes the cluster selector from selected records' current assignments.
 */
export const initializeSelectionOnInitiate = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.initiate.success.observe().pipe(
      rxjs.withLatestFrom(feature.selectors.assignmentRecords.asObservable),
      rxjs.map(([action, records]) => {
        if (records.length === 1) {
          const [record] = records

          // Single-record assignment should mirror that record's current
          // cluster, leaving the selector blank when the record is unassigned.
          if (TypeChecker.isUndefined(record?.clusterId)) {
            return plugin.clustersEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
              .from(action)
              .create({
                key: SPACE_RECORDS_SELECTION_TARGET,
                value: { release: true },
              })
          }

          // When the record is assigned to a cluster, preselect that cluster in
          // the selector.
          return plugin.clustersEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
            .from(action)
            .create({
              key: SPACE_RECORDS_SELECTION_TARGET,
              value: { set: [record.clusterId] },
            })
        }

        const originalClusterIds = new Set(
          records.map(record => record.clusterId),
        )
        const [originalClusterId] = [...originalClusterIds.values()]

        // Multi-record assignment should only preselect a cluster when every
        // selected record already points at the same cluster. Mixed assignments
        // stay blank so the user has to choose the new cluster explicitly.
        if (
          originalClusterIds.size !== 1 ||
          TypeChecker.isUndefined(originalClusterId)
        ) {
          return plugin.clustersEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
            .from(action)
            .create({
              key: SPACE_RECORDS_SELECTION_TARGET,
              value: { release: true },
            })
        }

        // When all selected records point at the same cluster, preselect that
        // cluster in the selector.
        return plugin.clustersEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
          .from(action)
          .create({
            key: SPACE_RECORDS_SELECTION_TARGET,
            value: { set: [originalClusterId] },
          })
      }),
    ),
)
